import { Button, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as Yup from 'yup';

import { showError, showToast } from '../../constants/toasts';
import { Loader } from '../../helpers';
import MainContainer from '../../layout/MainContainer';
import {
  useAddEngineCapacityMutation,
  useLazyGetEngineCapacityByIdQuery,
  useUpdateEngineCapacityMutation,
} from '../../service/engineCapacity';
import { CommonBody } from '../../types/General';
import { generateEncryptedKeyBody } from '../../utils/crypto';
import { isNumber } from '../../utils/validations';

const AddEngineCapacity = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [addEngineCapacity] = useAddEngineCapacityMutation();
  const [updateEngineCapacity] = useUpdateEngineCapacityMutation();
  const [getDetail] = useLazyGetEngineCapacityByIdQuery();

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      min: '',
      max: '',
    },

    validationSchema: Yup.object({
      min: Yup.number()
        .typeError('Minimum capacity must be a number')
        .required('This field is required')
        .lessThan(Yup.ref('max'), 'Minimum capacity must be less than Maximum capacity'),
      max: Yup.number()
        .typeError('Maximum capacity must be a number')
        .required('This field is required'),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);

      const body = {
        min: Number(values?.min),
        max: Number(values?.max),
      };

      if (id) {
        try {
          setLoading(true);
          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await updateEngineCapacity({
            body: encryptedBody,
            id,
          }).unwrap();
          setLoading(false);

          if (response?.statusCode === 200) {
            showToast('Engine capacity updated successfully');
            navigate('/vehicle-specifications/engine-capacity');
          }
        } catch (error: any) {
          setLoading(true);

          showError(error?.data?.message || '');
          console.error(error);
        }
      } else {
        try {
          setLoading(true);

          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await addEngineCapacity({
            body: encryptedBody,
          }).unwrap();
          setLoading(false);

          if (response?.statusCode === 200) {
            showToast('Engine capacity added successfully');
            navigate('/vehicle-specifications/engine-capacity');
          }
        } catch (error: any) {
          setLoading(false);
          showError(error?.data?.message || '');
          console.error(error);
        }
      }
    },
  });

  const getData = async (id: string) => {
    setLoading(true);
    try {
      const response = await getDetail({
        id,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        formik.setFieldValue('min', response?.data?.min);
        formik.setFieldValue('max', response?.data?.max);
      }
    } catch (error: any) {
      showError(error?.data?.message || '');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getData(id);
    }
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title fdx">
        <div>
          <h1>{id ? 'Edit' : 'Add'} Engine Capacity</h1>
          <p>
            <span onClick={() => navigate('/dashboard')}>Dashboard</span> -{' '}
            <span onClick={() => navigate('/vehicle-specifications/engine-capacity')}>
              Vehicle Specifications
            </span>{' '}
            -{' '}
            <span onClick={() => navigate('/vehicle-specifications/engine-capacity')}>
              Engine Capacity
            </span>{' '}
            - {id ? 'Edit' : 'Add'}
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_orange" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Minimum Capacity</label>
              <TextField
                variant="outlined"
                placeholder="Minimum Capacity"
                fullWidth
                hiddenLabel
                name="min"
                id="min"
                className="text_field"
                inputProps={{ maxLength: 5 }}
                value={formik.values.min}
                onChange={(val: any) => {
                  if (isNumber(val.target.value)) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.min && formik.errors.min}
              />
            </div>
            <div className="control_group w_50">
              <label>Maximum Capacity</label>
              <TextField
                variant="outlined"
                placeholder="Maximum Capacity"
                fullWidth
                hiddenLabel
                name="max"
                id="max"
                className="text_field"
                inputProps={{ maxLength: 5 }}
                value={formik.values.max}
                onChange={(val: any) => {
                  if (isNumber(val.target.value)) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.max && formik.errors.max}
              />
            </div>
          </div>

          <div className="form_end_btn">
            <Button className="btn btn_secondary" type="submit">
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddEngineCapacity;
